<template>
  <div class="dcm-root" v-if="isVigilante">
    <!-- FAB cuando no hay chats -->
    <transition name="dcm-fab-fade">
      <button
        v-if="allChats.length === 0"
        class="dcm-fab"
        @click="onFabClick"
        title="Chats directos"
        id="dcm-fab-btn"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span class="dcm-fab-label">Chats</span>
      </button>
    </transition>

    <!-- Panel de chats flotante -->
    <transition name="dcm-panel">
      <div v-if="allChats.length > 0" class="dcm-panel" :class="{ 'dcm-panel--overflow': allChats.length > 3 }">
        <!-- Indicador de overflow cuando hay muchos chats -->
        <div v-if="allChats.length > 3" class="dcm-overflow-indicator">
          <span>{{ allChats.length }} chats activos</span>
          <button class="dcm-collapse-btn" @click="collapseAll" title="Minimizar todos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          </button>
        </div>

        <!-- Lista de ventanas de chat -->
        <div class="dcm-chat-list" ref="chatList">
          <transition-group name="dcm-chat-item" tag="div" class="dcm-chat-items">
            <DirectChatWindow
              v-for="chat in allChats"
              :key="chat.localId"
              :chat="chat"
            />
          </transition-group>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DirectChatWindow from './DirectChatWindow.vue'

export default {
  name: 'DirectChatManager',

  components: {
    DirectChatWindow
  },

  computed: {
    ...mapGetters(['getProfile', 'isAuthenticated']),
    ...mapGetters('directChats', ['allChats', 'totalUnread']),

    isVigilante () {
      return this.isAuthenticated && this.getProfile && this.getProfile.role === 4
    }
  },

  watch: {
    isVigilante (val) {
      if (val) {
        this.initDirectChats()
      }
    }
  },

  mounted () {
    if (this.isVigilante) {
      this.initDirectChats()
    }
  },

  methods: {
    ...mapActions('directChats', ['initDirectChats']),

    onFabClick () {
      // El FAB no hace nada especial cuando no hay chats:
      // los chats se abren solo vía WebSocket start_chat
      // Pero podemos mostrar un tooltip o toast informativo
      this.$store.commit('snackbar_change_message', 'Los chats aparecen automáticamente cuando un residente los inicia')
      this.$store.commit('snackbar_change_color', 'info')
      this.$store.commit('snackbar_activate_true')
      setTimeout(() => {
        this.$store.commit('snackbar_activate_false')
      }, 3500)
    },

    collapseAll () {
      this.allChats.forEach(chat => {
        if (!chat.isMinimized) {
          this.$store.dispatch('directChats/toggleMinimize', chat.localId)
        }
      })
    }
  }
}
</script>

<style scoped>
/* ===== ROOT ===== */
.dcm-root {
  position: fixed;
  bottom: 0;
  right: 16px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
}

.dcm-root > * {
  pointer-events: auto;
}

/* ===== FAB ===== */
.dcm-fab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px 0 16px;
  height: 52px;
  background: linear-gradient(135deg, #4F46E5 0%, #6366F1 100%);
  border: none;
  border-radius: 26px 26px 0 0;
  box-shadow: 0 -2px 20px rgba(79, 70, 229, 0.35), 0 4px 16px rgba(0,0,0,0.12);
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', 'Roboto', sans-serif;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.22s ease;
  transform-origin: bottom center;
}

.dcm-fab svg {
  width: 22px;
  height: 22px;
  stroke: #fff;
}

.dcm-fab:hover {
  transform: translateY(-3px);
  box-shadow: 0 -4px 28px rgba(79, 70, 229, 0.45), 0 6px 20px rgba(0,0,0,0.14);
}

.dcm-fab-label {
  letter-spacing: 0.2px;
}

/* ===== FAB FADE ===== */
.dcm-fab-fade-enter-active,
.dcm-fab-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dcm-fab-fade-enter,
.dcm-fab-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* ===== PANEL ===== */
.dcm-panel {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: calc(100vw - 32px);
}

/* ===== OVERFLOW INDICATOR ===== */
.dcm-overflow-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #4F46E5 0%, #6366F1 100%);
  color: #fff;
  font-size: 12px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 8px 8px 0 0;
  margin-bottom: 4px;
  align-self: stretch;
  justify-content: space-between;
}

.dcm-collapse-btn {
  background: rgba(255,255,255,0.15);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.dcm-collapse-btn svg {
  width: 14px;
  height: 14px;
  stroke: #fff;
}

.dcm-collapse-btn:hover {
  background: rgba(255,255,255,0.25);
}

/* ===== CHAT LIST ===== */
.dcm-chat-list {
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 0;
}

.dcm-chat-list::-webkit-scrollbar {
  height: 4px;
}

.dcm-chat-list::-webkit-scrollbar-track {
  background: transparent;
}

.dcm-chat-list::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 2px;
}

.dcm-chat-items {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;
  padding: 0 0 0 0;
}

/* ===== CHAT ITEM TRANSITIONS ===== */
.dcm-chat-item-enter-active,
.dcm-chat-item-leave-active {
  transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dcm-chat-item-enter {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.dcm-chat-item-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

.dcm-chat-item-move {
  transition: transform 0.25s ease;
}

/* ===== PANEL TRANSITION ===== */
.dcm-panel-enter-active,
.dcm-panel-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.dcm-panel-enter,
.dcm-panel-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .dcm-root {
    right: 8px;
  }

  .dcm-chat-items {
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .dcm-root {
    right: 0;
    left: 0;
    align-items: stretch;
  }

  .dcm-panel {
    max-width: 100vw;
  }

  .dcm-chat-items {
    flex-direction: column;
    align-items: stretch;
  }

  .dcm-fab {
    border-radius: 16px 16px 0 0;
    width: 100%;
    justify-content: center;
  }
}
</style>
